import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { demeterChartsApi, demeterPageElementsApi } from '../../../Apis/Apis';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import {
    Currency,
    DemeterChartSeriesModel,
    DemeterChartSeriesSourceModel,
    DemeterPageType,
    GetDemeterChartSeriesDataRequest,
    MarketIndicatorTransformationType,
    UnitOfMeasure,
} from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import useApi from '../../Apis/Hooks/useApiHook';
import useMultipleApis from '../../Apis/Hooks/useMultipleApisHook';
import addNewDataSource from '../../Assets/Icons/expandGray.svg';
import { chartColors } from '../../Components/Charts/ChartDefinitions';
import PageHeader from '../../Components/Headers/PageHeader';
import CardSkeleton from '../../Components/Layout/CardSkeleton';
import InlineTab from '../../Components/Navigation/InlineTabs/InlineTab';
import InlineTabs from '../../Components/Navigation/InlineTabs/InlineTabs';
import useLanguage from '../../Services/Language/useLanguageHook';
import { transformationTypesRequiringValues } from '../Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import styles from './ChartBuilder.module.scss';
import ChartBuilderChartDisplay from './ChartBuilderChartDisplay/ChartBuilderChartDisplay';
import {
    ChartBuilderRequest,
    DataSourceSelection,
    defaultChartBuilderOptions,
    defaultChartStyle,
    defaultDataSource,
    leadingIndicatorTypeMap,
} from './ChartBuilderDefinitions';
import ChartBuilderChartOptionsSection from './ChartBuilderOptions/ChartBuilderChartOptions';
import ChartBuilderDataSeriesOptionsSection from './ChartBuilderOptions/ChartBuilderDataSeriesOptions';
import ChartBuilderLabelOptions from './ChartBuilderOptions/ChartBuilderLabelOptions';

const maximumNumberOfDataSeries = 2;
const minimumNumberOfDataSeries = 2;
const availableUnitOfMeasures = applicationConstants.AvailableGeneralUnitOfMeasures as UnitOfMeasure[];
const availableCurrencies = applicationConstants.AvailableCurrencies as Currency[];
const notAllowedPageTypes: DemeterPageType[] = [DemeterPageType.MonitorsAndIndicators, DemeterPageType.NewsAndSummaries, DemeterPageType.BalanceTable];

const ChartBuilderCreateChartPage: React.FC = () => {
    // Application and request hooks.
    const [translations] = useLanguage();
    const { demeterChartGuid } = useParams();

    // Request hooks.
    const [chartBuilderUpsertRequest, setChartBuilderUpsertRequest] = useState<ChartBuilderRequest>(defaultChartBuilderOptions);

    const isValidTransformationTypeAndValue = (dataSource: DemeterChartSeriesSourceModel) => {
        if (!dataSource.transformationValue || dataSource.transformationType === null || !dataSource.transformationType) {
            return true;
        }

        const rollingAverageValueInBounds =
            dataSource.transformationType === MarketIndicatorTransformationType.RollingAverage && dataSource.transformationValue! >= 3;
        const relativeStrenthIndexValueInBounds =
            dataSource.transformationType === MarketIndicatorTransformationType.RelativeStrengthIndex &&
            dataSource.transformationValue &&
            dataSource.transformationValue >= 5;
        const rollingAverageForwardContractsValueInBounds =
            dataSource.transformationType === MarketIndicatorTransformationType.RollingAverageForwardContracts &&
            dataSource.transformationValue! >= 2 &&
            dataSource.transformationValue! <= 5;
        const multiplyIsInBounds = dataSource.transformationType === MarketIndicatorTransformationType.Multiply && dataSource.transformationValue! > 0;

        const valueInBounds =
            rollingAverageValueInBounds || relativeStrenthIndexValueInBounds || rollingAverageForwardContractsValueInBounds || multiplyIsInBounds;
        const noValueRequired = !transformationTypesRequiringValues.includes(dataSource.transformationType);
        return !!(valueInBounds || noValueRequired);
    };

    // Api hooks.
    const [listDemeterChartDataLoading, refreshListDemeterChartData, listDemeterChartDataResponse] = useMultipleApis(
        () => {
            if (!chartBuilderUpsertRequest || !chartBuilderUpsertRequest.rows[0] || (!getDemeterChartResponse?.chart && demeterChartGuid)) {
                return null;
            }

            return (
                chartBuilderUpsertRequest.rows
                    .filter((x) => x.sources?.length)
                    .filter((x) => x.sources.every((y) => isValidTransformationTypeAndValue(y)))
                    .map((x) => {
                        if (x.dataCombinationType && x.sources.every((y) => y.region && y.commodity)) {
                            return x;
                        }

                        return { ...x, dataCombinationType: undefined };
                    })
                    .map((x) => ({
                        ...x,
                        sources: x.sources
                            .filter(
                                (y) =>
                                    (y.transformationValue && transformationTypesRequiringValues.includes(y.transformationType!)) ||
                                    !transformationTypesRequiringValues.includes(y.transformationType!),
                            )
                            .filter((y) => y.region && y.commodity),
                    }))
                    // TODO: Can remove dataSourceTags once its added as field on backend api.
                    .map((x) =>
                        demeterChartsApi.getDemeterChartSeries({
                            ...x,
                            sourceData: x.sources!.map((y) => ({ dataSourceTags: [], ...y })),
                        } as GetDemeterChartSeriesDataRequest),
                    )
            );
        },
        {
            errorMessage: translations.chartBuilder.messages.getChartSeriesFailure,
        },
    );

    const [, refreshGetDemeterChart, getDemeterChartResponse] = useApi(() => demeterChartsApi.getDemeterChart(demeterChartGuid ?? ''), {
        errorMessage: translations.chartBuilder.messages.getChartFailure,
    });

    const [, , listDemeterPageElementsResponse] = useApi(() =>
        demeterPageElementsApi.listDemeterPageElementsCurrent(undefined, undefined, undefined, undefined, undefined, 'en-US', false),
    );

    const pageElements = useMemo(() => {
        if (!listDemeterPageElementsResponse || !listDemeterPageElementsResponse.rows) {
            return [];
        }

        return listDemeterPageElementsResponse.rows.filter(
            (x) =>
                x.pageType !== DemeterPageType.CommodityIndicators &&
                x.pageType !== DemeterPageType.HedgeMonitorPro &&
                x.pageType !== DemeterPageType.Presentation &&
                x.pageType !== DemeterPageType.ValueMatrixCalculator,
        );
    }, [listDemeterPageElementsResponse]);

    const filteredPageTypePageElements = pageElements.filter((x) => !notAllowedPageTypes.includes(x.pageType!));

    useEffect(() => {
        if (pageElements.length === 0 || !getDemeterChartResponse?.chart || !demeterChartGuid) {
            return;
        }

        const savedChart = { ...defaultChartBuilderOptions, ...getDemeterChartResponse?.chart };
        const allDataSourceColorsInUse = savedChart.rows.map((y) => y.sources.map((z) => (z as DataSourceSelection).color)).flat();
        const allDataSeriesColorsInUse = savedChart.rows.map((y) => y.color);
        const allColorsInUse = [...allDataSeriesColorsInUse, ...allDataSourceColorsInUse];
        const currentChartColor = chartColors.lineChartColorsRuleSetOne.find((x) => !allColorsInUse.includes(x));

        while (savedChart.rows.length < minimumNumberOfDataSeries) {
            savedChart.rows.push({
                style: defaultChartStyle,
                sources: [{ ...defaultDataSource, color: currentChartColor }],
                rows: [],
            });
        }

        const savedChartsRows = savedChart.rows.map((x) => ({
            ...x,
            rows: [],
            sources: x.sources.map((y) => ({
                ...filteredPageTypePageElements?.find(
                    (z) =>
                        (z.commodity === y.commodity || z.subCommodity === y.commodity) &&
                        z.subRegion === y.subRegion &&
                        z.region === y.region &&
                        y.leadingIndicatorType === leadingIndicatorTypeMap.find((a) => a.pageType === z.pageType)?.leadingIndicatorType,
                ),
                ...y,
                rows: [],
                currency: availableCurrencies.includes(y.currency!) ? y.currency : undefined,
                unitOfMeasure: availableUnitOfMeasures.includes(y.unitOfMeasure!) ? y.unitOfMeasure : undefined,
                transformationValue: y.transformationValue ?? undefined,
            })),
        }));

        setChartBuilderUpsertRequest({ ...savedChart, rows: savedChartsRows });
    }, [getDemeterChartResponse, pageElements]);

    useEffect(() => {
        if (!demeterChartGuid) {
            return;
        }

        refreshGetDemeterChart();
    }, [demeterChartGuid]);

    useEffect(() => {
        const validRowsOfChartbuilderRequest = chartBuilderUpsertRequest?.rows
            .filter((x) => x.sources.every((y) => isValidTransformationTypeAndValue(y)))
            .some((x) => x.sources.every((y) => y.commodity && y.region));

        if (!validRowsOfChartbuilderRequest) {
            return;
        }

        refreshListDemeterChartData();
    }, [chartBuilderUpsertRequest]);

    const handleAddDataSeriesSection = () => {
        const dataSeries = [...chartBuilderUpsertRequest!.rows];
        setChartBuilderUpsertRequest({
            ...chartBuilderUpsertRequest!,
            rows: [...dataSeries, {} as DemeterChartSeriesModel],
        });
    };

    const loading = !listDemeterPageElementsResponse || !listDemeterChartDataResponse || !chartBuilderUpsertRequest;

    return loading ? (
        <CardSkeleton />
    ) : (
        <div className={styles.master_page_container}>
            <PageHeader
                title={translations.page.chartBuilder}
                rightSideComponent={
                    <Link className={styles.valorization_calculator_manage_saved} to={NavigationRoutes.ChartBuilderList}>
                        {translations.chartBuilder.text.manageSavedCharts}
                    </Link>
                }
            />
            <p className={styles.chart_builder_subheader}>{translations.chartBuilder.text.toGetStarted}</p>
            <div className={styles.chart_builder_split_page}>
                <div className={styles.chart_builder_left_side}>
                    <InlineTabs
                        inlineTabsKey="dataSeriesTab"
                        tabsRightSide={
                            chartBuilderUpsertRequest.rows.length < maximumNumberOfDataSeries ? (
                                <button type="button" onClick={handleAddDataSeriesSection} className={styles.chart_builder_choose_data_series_button}>
                                    <img src={addNewDataSource} alt="#" /> {translations.chartBuilder.text.addDataSeries}
                                </button>
                            ) : undefined
                        }
                        track={{ eventCategory: 'ChartBuilder', eventAction: 'Tab' }}
                    >
                        {chartBuilderUpsertRequest.rows.map((x, index) => (
                            <InlineTab
                                key={x.demeterChartSeriesGuid ?? index}
                                show
                                title={`${translations.chartBuilder.headers.series} ${index + 1}`}
                                inlineTabKey={`dataSeries${index}`}
                                testId={`dataSeries${index}`}
                            >
                                <ChartBuilderDataSeriesOptionsSection
                                    setChartBuilderUpsertRequest={setChartBuilderUpsertRequest}
                                    chartBuilderUpsertRequest={chartBuilderUpsertRequest}
                                    pageElements={filteredPageTypePageElements}
                                    listDemeterChartDataResponse={listDemeterChartDataResponse}
                                />
                            </InlineTab>
                        ))}
                        <InlineTab show title={translations.chartBuilder.headers.chartSettings} inlineTabKey="chartOptions" testId="ChartOptionsTab">
                            <>
                                <ChartBuilderLabelOptions
                                    chartBuilderRequest={chartBuilderUpsertRequest}
                                    setChartBuilderRequest={setChartBuilderUpsertRequest}
                                />
                                <ChartBuilderChartOptionsSection
                                    chartBuilderUpsertRequest={chartBuilderUpsertRequest}
                                    setChartBuilderUpsertRequest={setChartBuilderUpsertRequest!}
                                />
                            </>
                        </InlineTab>
                    </InlineTabs>
                </div>
                {listDemeterChartDataResponse && (
                    <div className={styles.chart_builder_right_side}>
                        <ChartBuilderChartDisplay
                            chartBuilderUpsertRequest={chartBuilderUpsertRequest}
                            setChartBuilderUpsertRequest={setChartBuilderUpsertRequest}
                            listDemeterChartDataResponse={listDemeterChartDataResponse!}
                            isValidTransformationTypeAndValue={isValidTransformationTypeAndValue}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChartBuilderCreateChartPage;
